import React, { ReactElement, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useHistory } from 'react-router'
import { IDocument } from 'kyc-core/dist/Interfaces'
import { useDispatch, useSelector } from 'react-redux'
import Spinner from '../Spinner'
import { deleteIcon } from '../../assets/images/images'
import ModalContainer from '../Modal/ModalContainer'
import {
  deleteDocument,
  getDocumentsByTypeIds,
  uploadDocuments,
} from '../../services/Kyc'
import { handleError } from '../../services/utils'
import MultiFilePreview from './MultiFilePreview'
import { ALERT_MESSAGES, ALLOW_SIZE } from '../../enums/common'
import { IRevenue } from '../../interfaces/Step2'
import { IStore } from '../../interfaces/Store'
import { IDocumentWithVerificationStatus } from '../../interfaces/Upload'
import { loadingActions } from '../../store/slices/Loading'

interface MultiFilesUploadProps {
  files: { index: number; valid: boolean; error?: string }[]
  setFiles: React.Dispatch<
    React.SetStateAction<{ index: number; valid: boolean; error?: string }[]>
  >
  multiple: boolean
  index: number
  revenue: IRevenue
  handleCardChange: (index: number, value: IRevenue) => void
  revenueCards: IRevenue[]
}

function MultipleFilesUpload({
  files,
  setFiles,
  multiple,
  index,
  revenue,
  handleCardChange,
  revenueCards,
}: MultiFilesUploadProps): ReactElement {
  const [currentFile, setCurrentFile] = useState<
    IDocumentWithVerificationStatus[]
  >([])
  const [isLoading, setIsLoading] = useState(false)
  const [fileError, setError] = useState()
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [documentToBeDeletedUid, setDocumentToBeDeletedUid] = useState<string>()
  const dispatch = useDispatch()

  const folderData = useSelector(
    (storeState: IStore) => storeState.folderReducer.folder
  )
  const revenuesDocumentUids = folderData?.revenue.flatMap(
    (rev) => rev.document_uid
  )

  const { register, watch, setValue } = useForm()
  const watchUpload = watch(`upload${index}`)
  const currentFiles = watchUpload
    ? (Array.from(watch(`upload${index}`)) as File[])
    : []
  const history = useHistory()

  const updateValidFiles = (valid: boolean, error?: string) => {
    const prevFiles = files.filter((file) => file.index !== index)
    setFiles([...prevFiles, { index, valid, error }])
  }

  const removeExtraDocuments = async (
    documentsList: IDocumentWithVerificationStatus[],
    lastUploadedDocumentUids: string[]
  ) => {
    setIsLoading(true)
    try {
      const otherCardsDocumentsUids = revenueCards.flatMap(
        (doc) => doc.document_uid
      )

      // Filter documents that should be kept
      const documentsToKeep = documentsList.filter((doc: IDocument) =>
        [
          ...otherCardsDocumentsUids,
          ...revenuesDocumentUids,
          ...lastUploadedDocumentUids,
        ].includes(doc.documentUid)
      )
      // Identify the documents to be removed
      const documentsToRemove = documentsList.filter(
        (doc) => !documentsToKeep.includes(doc)
      )

      //  Perform delete requests for each document that needs to be removed
      await Promise.all(
        documentsToRemove.map(async (doc) => {
          if (doc.documentUid) {
            try {
              await deleteDocument({ documentUid: doc.documentUid })
            } catch (e) {
              handleError(e, history) // Handle each error separately
            }
          }
        })
      )

      // Return the documents that should be kept
      return documentsToKeep
    } catch (e) {
      handleError(e, history)
    } finally {
      setDeleteModalOpen(false)
      setIsLoading(false)
    }
  }
  const getUploadedDocuments = async (documentUids: string[]) => {
    try {
      const documents = await getDocumentsByTypeIds([3])
      const processingDocuments = documents
        .map((document: IDocumentWithVerificationStatus) => ({ ...document }))
        .filter(
          ({ documentUid, status }: IDocumentWithVerificationStatus) =>
            !!documentUid && status !== 'invalid'
        )

      const filteredDocuments = (
        (await removeExtraDocuments(processingDocuments, documentUids)) || []
      ).filter(
        (doc) =>
          [...revenue.document_uid, ...documentUids].includes(
            doc.documentUid
          ) &&
          (doc.status === 'valid' || doc.status === 'pending')
      )
      const documentsAreUploaded = filteredDocuments.some(
        (document: IDocumentWithVerificationStatus) => document.documentUid
      )
      updateValidFiles(documentsAreUploaded)
      setCurrentFile(filteredDocuments)

      const filteredDocumentsUids = filteredDocuments.flatMap(
        (doc) => doc.documentUid
      )
      handleCardChange(index, {
        ...revenue,
        document_uid: filteredDocumentsUids,
      })
    } catch (e) {
      handleError(e, history)
    }
  }

  const onDocumentUpload = async () => {
    if (currentFiles.length === 0) {
      setError('Ce champ est obligatoire.')
      return
    }

    setIsLoading(true)
    setError('')
    try {
      let docUids: string[] = []

      await Promise.all(
        currentFiles.map((file) =>
          uploadDocuments(
            1,
            [file],
            [{ personTypeId: 1, typeId: 3 }],
            true
          ).then((doc) => {
            docUids = [...docUids, doc[0].documentUid]
          })
        )
      )
      handleCardChange(index, {
        ...revenue,
        document_uid: [...revenue.document_uid, ...docUids],
      })
      await getUploadedDocuments(docUids)
    } catch (e) {
      const error = String(e)
      const status = parseInt(
        error.substring(error.length - 3, error.length),
        10
      )

      if (status === 400) {
        setIsLoading(false)
        setError(ALERT_MESSAGES.fileUpload.message)
        return
      }

      handleError(e, history)
    } finally {
      setIsLoading(false)
    }
  }

  const onDeleteDocument = async (documentUid: string) => {
    setIsLoading(true)
    try {
      if (documentUid) {
        await deleteDocument({ documentUid })
        setCurrentFile(
          currentFile.filter((file) => file.documentUid !== documentUid)
        )
      }
    } catch (e) {
      handleError(e, history)
    } finally {
      setDeleteModalOpen(false)
      setIsLoading(false)
    }
  }
  useEffect(() => {
    if (folderData?.revenue?.[index]?.document_uid) {
      dispatch(loadingActions.loading())

      getUploadedDocuments([]).then(() => {
        dispatch(loadingActions.loaded())
      })
    }
  }, [])

  const currentCardError = files.find((file) => file.index === index)?.error

  if (isLoading) {
    return (
      <div className="dFlex justifyContentEnd">
        <div className="relative pb3 pl3 mb3">
          <Spinner small={true} />
        </div>
      </div>
    )
  }

  return (
    <div className="mb3">
      <p className="inputLabel">
        <span className="inputLabelRequired">Type de document à joindre</span>
      </p>
      <div className="textSecondaryBlack fontSizeSm fontWeightBold mb2">
        <p className="opacity50 mb-1">
          Dernier avis d’imposition et/ou Déclaration de revenus fonciers et/ou
          IFI
        </p>
        <p className="opacity50 fontSizeXs">
          Veuillez fournir votre avis d’impôt établi en 2024 sur vos revenus
          2023 (retrouvez ces informations en haut de la première page de votre
          avis d’impôt)
        </p>
        <p className="fontSizeXs mt1 opacity65">
          Veuillez transmettre vos documents en cliquant sur “Joindre les
          documents“, sélectionnez TOUS vos documents pour les télécharger en
          une fois.
        </p>
      </div>
      <div className="row">
        <div className="colSm6 textXxs opacity65 textSecondaryDark mb3 mb15md">
          <i>
            {multiple && (
              <>
                Nombre de documents max : 5<br />
              </>
            )}
            Formats acceptés : JPG, PNG, PDF <br />
            Taille max : 20 Mo
          </i>
        </div>
        <div className="colSm6 dFlex justifyContentEnd alignItemsCenter">
          <div>
            <input
              type="file"
              id={`upload${index}`}
              multiple={multiple}
              ref={register({ required: true })}
              name={`upload${index}`}
              onChange={(e) => {
                if (e.target.files) {
                  const filesToUpload = Array.from(e.target.files)
                  const totalFilesSize = filesToUpload.reduce(
                    (totalSize, file) => totalSize + file.size,
                    0
                  )
                  setError('')

                  if (totalFilesSize > ALLOW_SIZE) {
                    setError(ALERT_MESSAGES.fileSize.message)
                    setValue(`upload${index}`, '')
                    return
                  }
                  if (filesToUpload.length + currentFile.length > 5) {
                    setError(
                      'Vous pouvez sélectionner un maximum de 5 documents.'
                    )
                    setValue(`upload${index}`, '')
                  }
                }
              }}
              disabled={currentFile.length >= 5 || isLoading}
              className="hidden"
              accept="application/pdf, image/jpeg, image/png"
            />
            <div className="dFlex alignItemsCenter btnUploadWrapper">
              <label
                className={`btn btnUpload wFull wAutoSm dBlock mlAuto ${
                  currentFile.length >= 5 || isLoading ? 'btnDisabled' : ''
                }`}
                htmlFor={`upload${index}`}
              >
                {currentFiles.length > 0 ? (
                  currentFiles.map((file) => (
                    <p key={file.name} className="truncate">
                      {file.name}
                    </p>
                  ))
                ) : (
                  <>Joindre les documents</>
                )}
              </label>
            </div>
            {fileError && !currentCardError && (
              <p className="errorError mt1">{fileError}</p>
            )}
            {currentCardError && (
              <p className="errorError mt1">Ce champ est obligatoire.</p>
            )}
          </div>
        </div>
      </div>
      <div className="row mt3">
        {currentFile
          ?.filter(
            (file: IDocumentWithVerificationStatus) =>
              file.name &&
              (file.status === 'valid' || file.status === 'pending')
          )
          .map((file, documentIndex) => {
            return (
              <div
                key={file.name}
                className="colLg6 mb2 dFlex alignItemsCenter"
              >
                <span className="fontWeightBold fontSizeSm">
                  Document {documentIndex + 1} :
                </span>
                <p className="ml1 mr2 fontWeightBold textBlue truncate dInlineBlock">
                  {decodeURIComponent(file.name)}
                </p>
                <MultiFilePreview content={file.content} />
                {file.status !== 'valid' && (
                  <button
                    type="button"
                    onClick={() => {
                      setDocumentToBeDeletedUid(file.documentUid)
                      setDeleteModalOpen(true)
                    }}
                    className="pointer dInlineBlock ml0.5 btnUnstyled"
                  >
                    <img src={deleteIcon} alt="delete" />
                  </button>
                )}
              </div>
            )
          })}
      </div>
      <ModalContainer isOpen={deleteModalOpen}>
        <div className="modalDialog">
          <div className="px35 py35 fontSizeDefault textLeft">
            <p className="mb3">
              Êtes-vous sûr de vouloir supprimer le document chargé ?
            </p>
            <div className="textRight">
              <button
                type="button"
                className="btn btnPrimary mb15 mb0md btnPrimarySm"
                onClick={() => {
                  if (documentToBeDeletedUid) {
                    onDeleteDocument(documentToBeDeletedUid)
                  }
                }}
              >
                Oui
              </button>
              <button
                className="btnUnstyled textUnderline ml3"
                type="submit"
                onClick={() => setDeleteModalOpen(false)}
              >
                Non
              </button>
            </div>
          </div>
        </div>
      </ModalContainer>

      {currentFile.length < 5 && !isLoading && (
        <div className="row mt5">
          <div className="col12 dFlex">
            <button
              className="btn btnPrimary btnPrimaryMd wFull wAutoSm mlAuto"
              onClick={() => onDocumentUpload()}
              type="button"
            >
              Valider les documents
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default MultipleFilesUpload
